import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { Users, RefreshCw, Loader2 } from "lucide-react";

type StripeCustomer = {
  id: string;
  userId: string;
  stripeCustomerId: string;
  isPrimary: boolean;
  email: string | null;
  name: string | null;
  createdAt: Date;
  updatedAt: Date;
  userName: string | null;
  userEmail: string | null;
};

const formatDate = (date: Date | string | null) => {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString();
};

export function StripeCustomers() {
  const {
    data: customersData,
    isLoading: customersLoading,
    refetch: refetchCustomers,
  } = trpc.admin.listStripeCustomers.useQuery({ limit: 50 });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Stripe Customers
        </CardTitle>
        <CardDescription>
          Users linked to Stripe customer IDs. A user can have multiple Stripe customer IDs.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {customersLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-chromium-mist" />
          </div>
        ) : !customersData?.customers || customersData.customers.length === 0 ? (
          <div className="text-center py-8 text-chromium-mist">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No Stripe customers found</p>
            <p className="text-sm mt-2">
              Customer records will appear here when users complete checkouts
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Stripe Customer ID</TableHead>
                <TableHead>Primary</TableHead>
                <TableHead>Stripe Email</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(customersData.customers as StripeCustomer[]).map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-silica">
                        {customer.userName || "Unknown"}
                      </p>
                      <p className="text-xs text-chromium-mist">
                        {customer.userEmail || customer.userId}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-chromium-mist">
                    {customer.stripeCustomerId}
                  </TableCell>
                  <TableCell>
                    {customer.isPrimary ? (
                      <Badge variant="outline" className="text-neon-green border-neon-green">
                        Primary
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Secondary</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-chromium-mist">
                    {customer.email || "—"}
                  </TableCell>
                  <TableCell className="text-sm text-chromium-mist">
                    {formatDate(customer.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="mt-4 pt-4 border-t border-slate-core">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchCustomers()}
            disabled={customersLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${customersLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
